import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { money } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import { Drawer, Field, GstinInput, PhoneInput, Select, TextInput } from '../../components/ui/Form';

interface Supplier {
  id: string;
  code: string;
  name: string;
  supplier_type: string;
  phone: string | null;
  gstin: string | null;
  is_registered: boolean;
  is_active: boolean;
  outstanding: string;
  /** What they hold of the shop's money, paid ahead of a delivery. */
  advance_held?: string;
}

const TYPES = [
  { value: 'BULLION', label: 'Bullion dealer' },
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'ARTISAN', label: 'Artisan / karigar' },
  { value: 'REFINER', label: 'Refiner' },
  { value: 'STONE', label: 'Stone supplier' },
  { value: 'SERVICE', label: 'Service provider' },
];

const BLANK = { code: '', name: '', supplierType: 'BULLION', phone: '', gstin: '', isRegistered: true };

export function SuppliersPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [retiring, setRetiring] = useState<Supplier | null>(null);
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', search],
    queryFn: () => endpoints.parties.suppliers(search || undefined),
  });

  const close = (): void => {
    setOpen(false);
    setEditing(null);
    setForm({ ...BLANK });
    setError(null);
  };

  const create = useMutation({
    mutationFn: () =>
      editing
        ? // The code is not sent: a year of purchase registers points at it.
          api.patch(`/suppliers/${editing.id}`, {
            name: form.name,
            supplierType: form.supplierType,
            phone: form.phone || null,
            gstin: form.gstin || null,
            isRegistered: form.isRegistered,
          })
        : api.post('/suppliers', {
            code: form.code,
            name: form.name,
            supplierType: form.supplierType,
            ...(form.phone ? { phone: form.phone } : {}),
            ...(form.gstin ? { gstin: form.gstin } : {}),
            isRegistered: form.isRegistered,
          }),
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      close();
      void client.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: setError,
  });

  const retire = useMutation({
    mutationFn: (s: Supplier) => api.patch(`/suppliers/${s.id}`, { isActive: !s.is_active }),
    onSuccess: (_d, s) => {
      toast(s.is_active ? `${s.name} retired` : `${s.name} is back`);
      void client.invalidateQueries({ queryKey: ['suppliers'] });
      setRetiring(null);
    },
    onError: (e) => {
      setError(e);
      setRetiring(null);
    },
  });

  const startEdit = (s: Supplier): void => {
    setEditing(s);
    setForm({
      code: s.code,
      name: s.name,
      supplierType: s.supplier_type,
      phone: s.phone ?? '',
      gstin: s.gstin ?? '',
      isRegistered: s.is_registered,
    });
    setOpen(true);
  };

  const rows = (data?.data ?? []) as unknown as Supplier[];
  const owed = rows.reduce((a, r) => a + Number(r.outstanding), 0);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <>
      <PageHeader
        title="Suppliers"
        subtitle={rows.length > 0 ? `${rows.length} suppliers, ${money(owed.toFixed(2))} owed` : undefined}
        actions={
          <>
            <input
              className="field w-56"
              placeholder="Search name or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {can('supplier.create') ? (
              <button className="btn-primary whitespace-nowrap" onClick={() => setOpen(true)}>
                Add supplier
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={error} />

      {isLoading ? <Loading /> : null}
      {!isLoading && rows.length === 0 ? (
        <Empty
          message="No suppliers yet."
          hint="Add the bullion dealers, manufacturers and artisans you buy from. You will need one before recording a purchase."
          action={can('supplier.create') ? { label: 'Add supplier', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Code', 'Name', 'Type', 'Phone', 'GSTIN', 'We owe', 'Advance with them', '']}>
          {rows.map((s) => (
            <tr key={s.id} className={s.is_active === false ? 'opacity-60' : ''}>
              <td className="td num">{s.code}</td>
              <td className="td">{s.name}</td>
              <td className="td text-xs">{TYPES.find((t) => t.value === s.supplier_type)?.label ?? s.supplier_type}</td>
              <td className="td num">{s.phone ?? '—'}</td>
              <td className="td num text-xs">
                {s.gstin ?? (s.is_registered ? '—' : <span className="text-ink-soft">unregistered</span>)}
              </td>
              <td className="td num text-right">
                {Number(s.outstanding) > 0 ? (
                  <span className="text-danger">{money(s.outstanding)}</span>
                ) : (
                  <span className="text-ink-soft">—</span>
                )}
              </td>
              <td className="td num text-right">
                {Number(s.advance_held ?? 0) > 0 ? (
                  <Link
                    className="text-success underline decoration-success/30"
                    to={`/advances?side=SUPPLIER`}
                    title="Paid ahead; a purchase from them uses it first"
                  >
                    {money(s.advance_held!)}
                  </Link>
                ) : (
                  <span className="text-ink-soft">—</span>
                )}
              </td>
              <td className="td whitespace-nowrap text-right">
                {can('supplier.edit') && s.is_active !== false ? (
                  <button className="text-xs text-gold-700 underline" onClick={() => startEdit(s)}>
                    Edit
                  </button>
                ) : null}
                {can('document.void') ? (
                  <button className="ml-3 text-xs text-ink-soft underline" onClick={() => setRetiring(s)}>
                    {s.is_active === false ? 'Bring back' : 'Retire'}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer title={editing ? `Edit ${editing.name}` : 'Add a supplier'} onClose={close}>
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code" required>
                <TextInput
                  value={form.code}
                  onChange={(v) => setForm({ ...form, code: v })}
                  placeholder="SUP-01"
                  maxLength={16}
                  uppercase
                  allow={/[A-Z0-9\-_]/}
                  autoFocus={!editing}
                  disabled={!!editing}
                />
              </Field>
              <Field label="Type" required>
                <Select
                  value={form.supplierType}
                  onChange={(v) => setForm({ ...form, supplierType: v })}
                  options={TYPES}
                />
              </Field>
            </div>

            <Field label="Name" required>
              <TextInput
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Chennai Bullion"
                maxLength={200}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              </Field>
              <Field label="GSTIN" hint="Leave blank if they are not registered.">
                <GstinInput value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} />
              </Field>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.isRegistered}
                onChange={(e) => setForm({ ...form, isRegistered: e.target.checked })}
              />
              <span>
                Registered for GST
                <span className="block text-xs text-ink-soft">
                  Input tax cannot be claimed on a purchase from an unregistered supplier, and the system will refuse
                  it.
                </span>
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" disabled={!form.code || !form.name || create.isPending}>
                {create.isPending ? 'Saving…' : editing ? 'Save changes' : 'Save supplier'}
              </button>
              <button type="button" className="btn-ghost" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
      <ConfirmDialog
        open={!!retiring}
        tone={retiring?.is_active === false ? 'primary' : 'danger'}
        title={
          retiring?.is_active === false ? `Bring back ${retiring?.name ?? ''}?` : `Retire ${retiring?.name ?? ''}?`
        }
        message={
          retiring?.is_active === false
            ? 'They will be offered again when entering a purchase.'
            : 'Their purchases, payments and ledger stay exactly as they are, and anything still owed to them is still owed. They simply stop being offered on new purchases.'
        }
        confirmLabel={retiring?.is_active === false ? 'Bring them back' : 'Retire'}
        busy={retire.isPending}
        onCancel={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate(retiring)}
      />
    </>
  );
}
